import React, { useState } from 'react';
import { CheckCircle, TrendingUp, TrendingDown, Circle } from 'lucide-react';
import { DASHBOARD_SIGNUP_URL } from '@/config/links';

const benefits = [
  'Real-time shipment tracking',
  'Automated label generation',
  'Multi-carrier management',
  'Analytics and reporting',
  'Order management system',
  'Customer notifications'
];

const kpis = [
  { label: 'Shipped', value: '1,284', trend: '+12.4%', up: true },
  { label: 'Delivered', value: '93.3%', trend: '+2.1%', up: true },
  { label: 'On-Time', value: '96.4%', trend: '+0.8%', up: true },
  { label: 'RTO Rate', value: '3.1%', trend: '-1.6%', up: true }
];

const trendBars = [42, 58, 51, 70, 64, 82, 76];

const allocationMix = [
  { name: 'Delhivery', share: 42, color: 'bg-blue-800' },
  { name: 'Bluedart', share: 33, color: 'bg-emerald-500' },
  { name: 'Xpressbees', share: 25, color: 'bg-blue-400' }
];

const recentOrders = [
  { id: '#BDL10482', status: 'Delivered', pill: 'bg-emerald-100 text-emerald-700' },
  { id: '#BDL10481', status: 'In Transit', pill: 'bg-blue-100 text-blue-800' },
  { id: '#BDL10479', status: 'Processing', pill: 'bg-gray-100 text-gray-600' }
];

const DashboardSection = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* Decorative Elements with Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>

        {/* Animated Circular decorative elements */}
        <div className="absolute top-1/3 left-0 w-48 h-48 bg-gradient-to-br from-blue-100/40 to-transparent rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-1/3 right-0 w-64 h-64 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full blur-3xl animate-float-slower"></div>

        {/* Animated Geometric shapes */}
        <div className="absolute top-20 right-1/4 w-20 h-20 border border-blue-300/30 rounded-lg animate-rotate-slow"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Side - Content (40% width) */}
          <div className="lg:col-span-5">
            <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">Powerful Dashboard</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-6 text-gray-900 leading-tight">
              Manage Everything from
              <span className="block text-blue-800">One Central Hub</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Our intuitive dashboard gives you complete control over your shipping operations. Monitor, manage, and optimize your logistics with ease.
            </p>

            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 group">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700 font-medium group-hover:text-blue-800 transition-colors">{benefit}</span>
                </div>
              ))}
            </div>

            <button
              className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
            >
              Explore Dashboard
            </button>
          </div>

          {/* Right Side - Illustrated Dashboard Mockup (60% width) */}
          <div
            className="lg:col-span-7"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="relative group">
              {/* Decorative frame with animation */}
              <div className={`absolute -inset-4 bg-gradient-to-br from-blue-500/20 to-emerald-600/20 rounded-3xl blur-2xl transition-all duration-300 ${
                isHovered ? 'opacity-100 scale-105' : 'opacity-70'
              }`}></div>

              <div className={`relative bg-white rounded-2xl shadow-2xl border-2 border-blue-200 transition-all duration-300 overflow-hidden ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}>
                {/* Window chrome */}
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                    <span className="ml-3 text-xs font-semibold text-gray-500">Boxd Dashboard</span>
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs font-semibold text-emerald-700 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    Live Preview
                  </div>
                </div>

                <div className="p-5 sm:p-6 space-y-5">
                  {/* KPI tiles */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {kpis.map((kpi) => (
                      <div key={kpi.label} className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{kpi.label}</p>
                        <p className="mt-1 text-lg font-bold text-gray-900">{kpi.value}</p>
                        <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                          {kpi.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {kpi.trend}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Orders trend chart */}
                  <div className="rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Orders Trend</p>
                      <span className="text-[11px] font-semibold text-gray-400">Last 7 days</span>
                    </div>
                    <div className="flex items-end justify-between gap-2 h-24">
                      {trendBars.map((height, index) => (
                        <div
                          key={index}
                          className="flex-1 rounded-t-md bg-gradient-to-t from-blue-800 to-emerald-500 transition-all duration-700"
                          style={{ height: isHovered ? `${height}%` : `${Math.max(height - 20, 15)}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Allocation mix */}
                    <div className="rounded-xl border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Allocation Mix</p>
                      <div className="space-y-2.5">
                        {allocationMix.map((courier) => (
                          <div key={courier.name}>
                            <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                              <span>{courier.name}</span>
                              <span className="text-gray-500">{courier.share}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${courier.color} transition-all duration-700`}
                                style={{ width: isHovered ? `${courier.share}%` : '0%' }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent orders */}
                    <div className="rounded-xl border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Recent Orders</p>
                      <div className="space-y-2.5">
                        {recentOrders.map((order) => (
                          <div key={order.id} className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700">{order.id}</span>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${order.pill}`}>
                              <Circle className="w-1.5 h-1.5 fill-current" />
                              {order.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(20px); }
        }

        @keyframes float-slower {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(30px) translateX(-20px); }
        }

        @keyframes rotate-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }

        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }

        .animate-rotate-slow {
          animation: rotate-slow 20s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default DashboardSection;
